import React, { useState, useContext } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import AuthContext from "../../../context/AuthContext";
import { fetchData } from "../../../services/apiService";
import { saveAs } from "file-saver";

const flattenData = (data) => {
  // Devuelve un array de objetos planos para la tabla
  const rows = [];
  Object.entries(data).forEach(([acquirer, isoObj]) => {
    Object.entries(isoObj).forEach(([iso, merchants]) => {
      Object.entries(merchants).forEach(([merchant, products]) => {
        Object.entries(products).forEach(([product, arr]) => {
          arr.forEach((item) => {
            rows.push({
              acquirer,
              iso,
              merchant,
              product,
              ...item,
            });
          });
        });
      });
    });
  });
  return rows;
};

const mergeTableRows = (rows, key, groupKeys = []) => {
  // Devuelve un array con info de rowspan para cada celda, considerando agrupamiento por claves superiores
  const result = [];
  let lastValue = null;
  let lastIndex = 0;
  let span = 1;
  let lastGroup = null;
  for (let i = 0; i < rows.length; i++) {
    const group = groupKeys.map((k) => rows[i][k]).join("||");
    if (rows[i][key] === lastValue && group === lastGroup) {
      span++;
      result[lastIndex].rowSpan = span;
      result.push({ rowSpan: 0 });
    } else {
      lastValue = rows[i][key];
      lastGroup = group;
      lastIndex = i;
      span = 1;
      result.push({ rowSpan: 1 });
    }
  }
  return result;
};

const InfoTreeMui = ({ setSnackOpen, setMessageOpen, setSeverity }) => {
  const { token, acquirerData } = useContext(AuthContext);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tableRows, setTableRows] = useState([]);
  const today = new Date().toISOString().slice(0, 10); // yyyy-mm-dd

  const handleSearch = async (e) => {
    e.preventDefault();
    if (startDate && endDate && endDate < startDate) {
      const dateErrorMessage = "End date cannot be before start date.";
      setSnackOpen(true);
      setMessageOpen(dateErrorMessage);
      setSeverity("error");
      return;
    }
    if ((startDate && startDate > today) || (endDate && endDate > today)) {
      const dateErrorMessage =
        "Start date and end date cannot be in the future.";
      setSnackOpen(true);
      setMessageOpen(dateErrorMessage);
      setSeverity("error");
      return;
    }
    try {
      const data = {
        startDate: startDate.replaceAll("-", ""),
        endDate: endDate.replaceAll("-", ""),
      };
      const headers = {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        acquirerName: acquirerData?.acquirerName,
        acquirerID: acquirerData?.acquirerId,
      };
      const response = await fetchData({
        endpoint: "/periodic-admin-transactions",
        data,
        headers,
        token,
      });
      if (response && response.data) {
        const rows = flattenData(response.data);
        rows.sort(
          (a, b) =>
            a.acquirer.localeCompare(b.acquirer) ||
            a.iso.localeCompare(b.iso) ||
            a.merchant.localeCompare(b.merchant) ||
            a.product.localeCompare(b.product)
        );

        const displayRows = [];
        let currentAcquirer = null;
        let acquirerSumAmount = 0;
        let acquirerSumCount = 0;

        rows.forEach((row) => {
          if (row.acquirer !== currentAcquirer) {
            if (currentAcquirer !== null) {
              displayRows.push({
                isSubtotal: true,
                acquirer: currentAcquirer,
                iso: "",
                merchant: "",
                product: "Subtotal",
                amount: acquirerSumAmount,
                currency_code: "",
                count: acquirerSumCount,
              });
            }
            currentAcquirer = row.acquirer;
            acquirerSumAmount = 0;
            acquirerSumCount = 0;
          }
          displayRows.push(row);
          acquirerSumAmount += Number(row.amount) || 0;
          acquirerSumCount += Number(row.count) || 0;
        });

        if (currentAcquirer !== null) {
          displayRows.push({
            isSubtotal: true,
            acquirer: currentAcquirer,
            iso: "",
            merchant: "",
            product: "Subtotal",
            amount: acquirerSumAmount,
            currency_code: "",
            count: acquirerSumCount,
          });
        }

        setTableRows(displayRows);
      } else {
        setTableRows([]);
        const errorMessage = "No data for this date range.";
        setSnackOpen(true);
        setMessageOpen(errorMessage);
        setSeverity("error");
      }
    } catch (err) {
      const errorMessage = "Error fetching data.";
      setSnackOpen(true);
      setMessageOpen(errorMessage);
      setSeverity("error");
      setTableRows([]);
    }
  };

  // Función para exportar a CSV con agrupación visual
  const exportToCSV = () => {
    if (!tableRows.length) return;
    const header = [
      "Acquirer",
      "ISO",
      "Merchant",
      "Product",
      "Amount",
      "Currency",
      "Count",
    ];
    const csvRows = [header.join(",")];
    tableRows.forEach((row, idx) => {
      if (row.isSubtotal) {
        csvRows.push(
          [
            row.acquirer,
            "",
            "",
            "Subtotal",
            row.amount,
            "",
            row.count,
          ]
            .map((v) => `"${v ?? ""}"`)
            .join(",")
        );
      } else {
        const acquirer = acquirerSpans[idx].rowSpan > 0 ? row.acquirer : "";
        const iso = isoSpans[idx].rowSpan > 0 ? row.iso : "";
        const merchant = merchantSpans[idx].rowSpan > 0 ? row.merchant : "";
        csvRows.push(
          [
            acquirer,
            iso,
            merchant,
            row.product,
            row.amount,
            row.currency_code,
            row.count,
          ]
            .map((v) => `"${v ?? ""}"`)
            .join(",")
        );
      }
    });
    csvRows.push(
      [
        "",
        "",
        "",
        "Totals",
        tableRows.reduce((sum, r) => r.isSubtotal ? sum : sum + (Number(r.amount) || 0), 0),
        "",
        tableRows.reduce((sum, r) => r.isSubtotal ? sum : sum + (Number(r.count) || 0), 0),
      ]
        .map((v) => `"${v ?? ""}"`)
        .join(",")
    );
    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `transactions_${startDate || "all"}_${endDate || "all"}.csv`);
  };

  // Calcula rowSpan para cada columna, respetando la jerarquía de agrupamiento
  const acquirerSpans = mergeTableRows(tableRows, "acquirer");
  const isoSpans = mergeTableRows(tableRows, "iso", ["acquirer"]);
  const merchantSpans = mergeTableRows(tableRows, "merchant", [
    "acquirer",
    "iso",
  ]);

  const totalAmount = tableRows.reduce(
    (sum, row) => row.isSubtotal ? sum : sum + (Number(row.amount) || 0),
    0
  );
  const totalCount = tableRows.reduce(
    (sum, row) => row.isSubtotal ? sum : sum + (Number(row.count) || 0),
    0
  );

  return (
    <Box
      sx={{
        fontFamily: "Arial, sans-serif",
        fontSize: 14,
        p: 2,
        maxWidth: 1200,
        margin: "0 auto",
      }}
    >
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Search by Date Range
          </Typography>
          <form onSubmit={handleSearch}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  inputProps={{ max: today }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  inputProps={{ max: today }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ height: 40 }}
                >
                  Search
                </Button>
              </Grid>
              {tableRows.length > 0 && (
                <Grid item xs={12} sm={4} md={3}>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    sx={{ height: 40 }}
                    onClick={exportToCSV}
                  >
                    Export CSV
                  </Button>
                </Grid>
              )}
            </Grid>
            {/* {error && (
              <Typography color="error" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )} */}
          </form>
        </CardContent>
      </Card>
      <Divider sx={{ mb: 3 }} />
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Results Table
        </Typography>
        <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    background: (theme) => theme.palette.primary.main,
                    color: "common.white",
                    fontWeight: 700,
                    borderRight: (theme) =>
                      `2.5px solid ${theme.palette.primary.dark}`,
                  }}
                >
                  Acquirer
                </TableCell>
                <TableCell
                  sx={{
                    background: (theme) => theme.palette.secondary.main,
                    color: "common.white",
                    fontWeight: 600,
                    borderRight: (theme) =>
                      `2px solid ${theme.palette.secondary.dark}`,
                  }}
                >
                  ISO
                </TableCell>
                <TableCell
                  sx={{
                    background: (theme) => theme.palette.blokko.taglineMist,
                    color: (theme) => theme.palette.primary.dark,
                    fontWeight: 500,
                    borderRight: (theme) =>
                      `1.5px solid ${theme.palette.primary.main}`,
                  }}
                >
                  Merchant
                </TableCell>
                <TableCell
                  sx={{
                    background: (theme) => theme.palette.blokko.taglinePeriwinkle,
                    color: (theme) => theme.palette.primary.dark,
                    fontWeight: 500,
                    borderRight: (theme) =>
                      `1px solid ${theme.palette.blokko.taglineBlue}`,
                  }}
                >
                  Product
                </TableCell>
                <TableCell
                  sx={{
                    background: (theme) => theme.palette.blokko.taglinePeriwinkle,
                    color: (theme) => theme.palette.primary.dark,
                    fontWeight: 500,
                  }}
                >
                  Amount
                </TableCell>
                <TableCell
                  sx={{
                    background: (theme) => theme.palette.blokko.taglinePeriwinkle,
                    color: (theme) => theme.palette.primary.dark,
                    fontWeight: 500,
                  }}
                >
                  Currency
                </TableCell>
                <TableCell
                  sx={{
                    background: (theme) => theme.palette.blokko.taglinePeriwinkle,
                    color: (theme) => theme.palette.primary.dark,
                    fontWeight: 500,
                  }}
                >
                  Count
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableRows.map((row, idx) => {
                if (row.isSubtotal) {
                  return (
                    <TableRow key={idx} sx={{ bgcolor: "blokko.tableSubtotal" }}>
                      <TableCell
                        colSpan={3}
                        sx={{
                          fontWeight: 600,
                          textAlign: "right",
                          borderRight: (theme) =>
                            `2.5px solid ${theme.palette.primary.main}`,
                        }}
                      >
                        Subtotal for {row.acquirer}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {row.amount}
                      </TableCell>
                      <TableCell />
                      <TableCell sx={{ fontWeight: 600 }}>
                        {row.count}
                      </TableCell>
                    </TableRow>
                  );
                }

                const isAcquirerGroup = acquirerSpans[idx].rowSpan > 0;
                const isIsoGroup = isoSpans[idx].rowSpan > 0;
                const isMerchantGroup = merchantSpans[idx].rowSpan > 0;
                return (
                  <TableRow
                    key={idx}
                    sx={{
                      borderTop: isAcquirerGroup
                        ? (theme) => `3px solid ${theme.palette.primary.main}`
                        : isIsoGroup
                        ? (theme) => `2px solid ${theme.palette.secondary.main}`
                        : isMerchantGroup
                        ? (theme) =>
                            `1.5px solid ${theme.palette.primary.light}`
                        : undefined,
                      bgcolor: isAcquirerGroup
                        ? "blokko.tableRow"
                        : isIsoGroup
                        ? "blokko.tableRowAlt"
                        : isMerchantGroup
                        ? "blokko.taglineMist"
                        : undefined,
                    }}
                  >
                    {isAcquirerGroup && (
                      <TableCell
                        rowSpan={acquirerSpans[idx].rowSpan}
                        sx={{
                          fontWeight: 700,
                          bgcolor: "blokko.tableRow",
                          borderRight: (theme) =>
                            `2.5px solid ${theme.palette.primary.main}`,
                          color: "primary.main",
                        }}
                      >
                        {row.acquirer}
                      </TableCell>
                    )}
                    {isIsoGroup && (
                      <TableCell
                        rowSpan={isoSpans[idx].rowSpan}
                        sx={{
                          fontWeight: 600,
                          bgcolor: "blokko.tableRowAlt",
                          borderRight: (theme) =>
                            `2px solid ${theme.palette.secondary.main}`,
                          color: "primary.main",
                        }}
                      >
                        {row.iso}
                      </TableCell>
                    )}
                    {isMerchantGroup && (
                      <TableCell
                        rowSpan={merchantSpans[idx].rowSpan}
                        sx={{
                          fontWeight: 500,
                          bgcolor: "blokko.taglineMist",
                          borderRight: (theme) =>
                            `1.5px solid ${theme.palette.primary.light}`,
                          color: "text.primary",
                        }}
                      >
                        {row.merchant}
                      </TableCell>
                    )}
                    <TableCell>{row.product}</TableCell>
                    <TableCell>{row.amount}</TableCell>
                    <TableCell>{row.currency_code}</TableCell>
                    <TableCell>{row.count}</TableCell>
                  </TableRow>
                );
              })}
              {tableRows.length > 0 && (
                <TableRow sx={{ bgcolor: "blokko.tableTotal" }}>
                  <TableCell colSpan={4} sx={{ fontWeight: 700, textAlign: "right" }}>
                    Totals
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{totalAmount}</TableCell>
                  <TableCell />
                  <TableCell sx={{ fontWeight: 700 }}>{totalCount}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default InfoTreeMui;
